import Dialog from "../../dialog/Dialog";
import Tooltip from "../../common/Tooltip";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  listByGroup,
  changeGroupName,
  copyPackage,
  deletePackage,
} from "../../../services/packageService";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import { ExpandableText } from "../../common/adminTable";
import { paginateClientList } from "../../../utils/clientPagination";

function SubPackages({
  open,
  onClose,
  packageGroupId,
  groupName: initialGroupName,
  packageCount = 0,
  onAddCombination,
}) {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [showToaster, setShowToaster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterType, setToasterType] = useState("success");
  const [copyLoading, setCopyLoading] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const queryClient = useQueryClient();
  const [deleteToaster, setDeleteToaster] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: subPackages,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["subPackages", packageGroupId],
    queryFn: () => listByGroup(packageGroupId),
  });

  // Delete package mutation
  const deleteMutation = useMutation({
    mutationFn: deletePackage,
    onSuccess: () => {
      queryClient.invalidateQueries(["subPackages", packageGroupId]);
      setDeleteToaster({
        message: "Package deleted successfully",
        type: "success",
        show: true,
      });
    },
    onError: (error) => {
      setDeleteToaster({
        message: "Error deleting package",
        type: "error",
        show: true,
      });
    },
  });

  const viewHandler = (id) => {
    onClose();
    navigate(`/packages/${id}/edit`);
  };

  const deleteHandler = async (id, name) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      console.error("Error deleting package:", error);
    }
  };

  useEffect(() => {
    if (subPackages?.data?.data) {
      setData(subPackages.data.data);
    }
  }, [subPackages]);

  const { data: pagedPackages, pagination } = useMemo(
    () => paginateClientList(data, currentPage),
    [data, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [data, packageGroupId]);

  useEffect(() => {
    // Close modal if query fails with 402 error
    if (error && error.status === 404) {
      onClose();
    }
  }, [error, onClose]);

  useEffect(() => {
    setGroupName(initialGroupName);
  }, [initialGroupName]);

  const handleClose = () => {
    onClose();
  };

  const handleNameChange = (e) => {
    setGroupName(e.target.value);
    setUpdateError(""); // Clear any previous errors when user starts typing
  };

  const handleUpdateName = async () => {
    if (!groupName.trim()) {
      setUpdateError("Group name cannot be empty");
      return;
    }

    try {
      setUpdateLoading(true);
      setUpdateError("");
      await changeGroupName(packageGroupId, groupName);

      // Invalidate and refetch queries that might be affected
      await queryClient.invalidateQueries(["subPackages", packageGroupId]);
      await queryClient.invalidateQueries(["packageGroup"]);

      setIsEditing(false);
      setShowToaster(true);
    } catch (error) {
      setUpdateError(
        error.response?.data?.message || "Failed to update group name"
      );
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCopyPackage = async (packageId) => {
    try {
      setCopyLoading(packageId);
      await copyPackage(packageId);

      // Refetch the data
      await queryClient.invalidateQueries(["subPackages", packageGroupId]);
      await queryClient.invalidateQueries(["packageGroup"]);

      setToasterMessage("Package copied successfully!");
      setToasterType("success");
      setShowToaster(true);
    } catch (error) {
      setToasterMessage(
        error.response?.data?.message || "Failed to copy package"
      );
      setToasterType("error");
      setShowToaster(true);
    } finally {
      setCopyLoading(null);
    }
  };

  const handleDeletePackage = async (packageId) => {
    try {
      setDeleteLoading(packageId);
      await deletePackage(packageGroupId, packageId);
      await queryClient.invalidateQueries(["subPackages", packageGroupId]);
      await queryClient.invalidateQueries(["packageGroup"]);
      setToasterMessage("Package deleted successfully!");
      setToasterType("success");
      setShowToaster(true);
      setData(data.filter((pkg) => pkg.id !== packageId));
    } catch (error) {
      setToasterMessage(
        error.response?.data?.message || "Failed to delete package"
      );
      setToasterType("error");
      setShowToaster(true);
    } finally {
      setDeleteLoading(null);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        title="Combinations Packages"
        maxWidth="4xl"
        maxHeight="80vh"
      >
        <div className="flex min-h-0 flex-col gap-5 px-1 py-1">
          {isLoading && (
            <div className="flex items-center justify-center py-10 text-sm text-gray-500">
              <i className="fi fi-rr-spinner animate-spin mr-2" />
              Loading combinations...
            </div>
          )}

          {error && !isLoading && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error.message || "Failed to load combinations"}
            </div>
          )}

          {!isLoading && !error && (
            <>
              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                <label
                  htmlFor="groupName"
                  className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Package Group Name
                </label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    {isEditing ? (
                      <input
                        id="groupName"
                        type="text"
                        value={groupName}
                        onChange={handleNameChange}
                        className={`min-w-0 flex-1 rounded-lg border bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                          updateError
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-200 focus:border-primary"
                        }`}
                        placeholder="Enter package group name"
                        disabled={updateLoading}
                      />
                    ) : (
                      <div className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900">
                        <ExpandableText
                          text={groupName}
                          lines={2}
                          label="full group name"
                          emptyText="No group name"
                        />
                      </div>
                    )}
                    {!isEditing ? (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-violet-100 bg-violet-50 text-violet-500 transition hover:bg-violet-100"
                        title="Edit group name"
                      >
                        <i className="fi fi-rr-pencil text-sm" />
                      </button>
                    ) : (
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={handleUpdateName}
                          disabled={updateLoading}
                          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition ${
                            updateLoading
                              ? "cursor-not-allowed border-gray-200 text-gray-400"
                              : "border-green-200 bg-green-50 text-green-600 hover:bg-green-100"
                          }`}
                          title="Save"
                        >
                          {updateLoading ? (
                            <i className="fi fi-rr-spinner animate-spin text-sm" />
                          ) : (
                            <i className="fi fi-rr-check text-sm" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditing(false);
                            setGroupName(initialGroupName);
                            setUpdateError("");
                          }}
                          disabled={updateLoading}
                          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition ${
                            updateLoading
                              ? "cursor-not-allowed border-gray-200 text-gray-400"
                              : "border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                          }`}
                          title="Cancel"
                        >
                          <i className="fi fi-rr-cross text-sm" />
                        </button>
                      </div>
                    )}
                  </div>
                  {updateError && (
                    <p className="text-sm text-red-500">{updateError}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <Tooltip
                  message="Maximum 2 combinations allowed"
                  position="top"
                  show={packageCount >= 4}
                >
                  <button
                    type="button"
                    onClick={onAddCombination}
                    disabled={packageCount >= 4}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      packageCount >= 4
                        ? "cursor-not-allowed text-gray-400 opacity-50"
                        : "bg-primary text-white hover:bg-primary/90"
                    }`}
                  >
                    <i className="fi fi-rr-plus text-sm" />
                    Add Combination
                  </button>
                </Tooltip>
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] table-fixed divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-14 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          ID
                        </th>
                        <th className="w-[38%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Name
                        </th>
                        <th className="w-32 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Tour Type
                        </th>
                        <th className="w-24 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Members
                        </th>
                        <th className="w-36 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Status
                        </th>
                        <th className="w-32 px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {pagedPackages.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-10 text-center text-sm text-gray-500"
                          >
                            No combination packages found in this group.
                          </td>
                        </tr>
                      ) : (
                        pagedPackages.map((pkg) => (
                          <tr key={pkg.id} className="align-middle hover:bg-gray-50/80">
                            <td className="px-3 py-3 text-sm font-medium text-gray-700">
                              {pkg.id}
                            </td>
                            <td className="px-3 py-3">
                              <ExpandableText
                                text={pkg.name}
                                label="full package name"
                                className="text-sm font-medium text-gray-900"
                              />
                            </td>
                            <td className="px-3 py-3 text-sm capitalize text-gray-700">
                              <span className="line-clamp-2" title={pkg.tour_type?.replace(/_/g, " ")}>
                                {pkg.tour_type?.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-700">
                              {pkg.minimum_members} - {pkg.maximum_members}
                            </td>
                            <td className="px-3 py-3">
                              {pkg.current_stage !== 9 ? (
                                <span className="inline-flex whitespace-nowrap items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                                  Draft
                                </span>
                              ) : pkg.is_approved ? (
                                <span className="inline-flex whitespace-nowrap items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                  Approved
                                </span>
                              ) : (
                                <span className="inline-flex whitespace-nowrap items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                                  Not Approved
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => viewHandler(pkg.id)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-violet-100 bg-violet-50 text-violet-500 transition hover:bg-violet-100"
                                  title="Edit package"
                                >
                                  <i className="fi fi-rr-pencil text-xs" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleCopyPackage(pkg.id)}
                                  disabled={copyLoading === pkg.id}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-blue-100 bg-blue-50 text-blue-500 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                                  title="Copy package"
                                >
                                  {copyLoading === pkg.id ? (
                                    <i className="fi fi-rr-spinner animate-spin text-xs" />
                                  ) : (
                                    <i className="fi fi-rr-copy text-xs" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeletePackage(pkg.id)}
                                  disabled={deleteLoading === pkg.id}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-500 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                                  title="Delete package"
                                >
                                  {deleteLoading === pkg.id ? (
                                    <i className="fi fi-rr-spinner animate-spin text-xs" />
                                  ) : (
                                    <i className="fi fi-rr-trash text-xs" />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {pagination.total > 0 && (
                  <div className="border-t border-gray-200 bg-gray-50/50 px-3 py-3">
                    <Pagination
                      pagination={pagination}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Dialog>

      {showToaster && (
        <Toster
          message={toasterMessage}
          type={toasterType}
          duration={3000}
          onClose={() => setShowToaster(false)}
        />
      )}

      {deleteToaster && (
        <Toster
          message={deleteToaster.message}
          type={deleteToaster.type}
          onClose={() => setDeleteToaster(false)}
        />
      )}
    </>
  );
}

export default SubPackages;
